// Fleet's agents and models (components/FleetList.jsx), from the range the breakdown reads
// (lib/fleetBreakdown.js): one register of agents or models, the totals over it,
// and its sessions split by kind. Sessions are the only measure. The range's
// sessions and each agent's are the Overview's (exactGroups), each session
// once; a model's, each session that called it, so models can add up to more
// than the range.
import { installedKey } from "./fleetConsole.js";
import { KINDS, MIN_SHARE, fold, installedNotUsed, kindOf } from "./fleetBreakdown.js";
import { apportion } from "./overview.js";

// A subject's rows, and what each row used.
export const SUBJECTS = {
  agents: { level: "agent", used: "model" },
  models: { level: "model", used: "agent" },
};
// Rows named before the rest fold into one, and how many each unfolding adds.
export const SHOWN = 12;
export const STEP = 50;
// A row's bar names this many of what it used; the rest are one part.
export const SEGMENTS = 6;

const sum = (list) => list.reduce((n, r) => n + r.sessions, 0);
const bySessions = (a, b) => b.sessions - a.sessions || a.name.localeCompare(b.name);
const within = (scope) => (r) => !scope || r[scope.by] === scope.id;

/**
 * A span's groups (lib/fleetBreakdown.js toPaths) counted as the Overview counts
 * agents (lib/overview.js agentsOf, each agent's traces in `field`, "now" or
 * "before"): an agent's traces are shared out over its groups by their own
 * counts, so a session whose frames fall in two groups counts once, a group
 * that names two agents is split as the Overview splits it, and every agent
 * has the Overview's count. An agent the groups do not hold (read a moment
 * apart) keeps its traces in one group of its own.
 */
export function exactGroups(groups, agents, field) {
  const by = new Map();
  for (const g of groups) {
    if (!by.has(g.agent)) by.set(g.agent, []);
    by.get(g.agent).push(g);
  }
  return agents.flatMap((a) => {
    const own = by.get(a.key) || [{ kind: kindOf({}), place: "remote", agent: a.key, agentName: a.name, model: "", modelName: "", provider: "", sessions: 1 }];
    const split = apportion(a[field] || 0, own.map((g) => g.sessions));
    return own.map((g, i) => ({ ...g, sessions: split[i] })).filter((g) => g.sessions > 0);
  });
}

/** A share as the breakdown writes it: 0%, <0.1%, one decimal under 1%, else whole. */
export function shareText(part, whole) {
  const s = whole ? (part / whole) * 100 : 0;
  return s === 0 ? "0%" : s < 0.1 ? "<0.1%" : s < 1 ? `${s.toFixed(1)}%` : `${Math.round(s)}%`;
}

/** The block's sessions by kind, each session once, every kind in its order. */
export function bandOf(block) {
  const at = new Map();
  for (const r of block?.groups || []) at.set(r.kind, (at.get(r.kind) || 0) + r.sessions);
  return Object.entries(KINDS).map(([id, name]) => ({ id, name, sessions: at.get(id) || 0 }));
}

/**
 * The totals over the register, within one kind (`scope`, { by, id }) or the
 * whole: sessions, distinct agents, distinct models, and installed products
 * with nothing of theirs in the block. Those have no kind, so a kind does not
 * narrow them. null where unread.
 */
export function totalsOf(block, scope = null, installed = null) {
  if (!block) return null;
  const keep = within(scope);
  const groups = block.groups.filter(keep);
  return {
    sessions: scope ? sum(groups) : block.sessions,
    agents: new Set(groups.map((r) => r.agent)).size,
    models: new Set(block.links.filter(keep).map((r) => r.model).filter(Boolean)).size,
    unused: installed ? installedNotUsed(installed, block.groups).length : null,
  };
}

/**
 * The register's rows within a kind or the whole, the most sessions first.
 * Each has the kinds it ran in, the most sessions first (`parts`), what it
 * used with the sessions of each (`used`: an agent's models, a model's
 * agents), and an agent its installed copies (`installed`, endpoints) where
 * the inventory was read.
 */
export function rowsOf(subject, block, scope = null, installed = null) {
  if (!block) return [];
  const { level, used } = SUBJECTS[subject];
  const keep = within(scope);
  const counted = (level === "agent" ? block.groups : block.links).filter(keep);
  const links = level === "agent" ? block.links.filter(keep) : counted;
  const parts = new Map();
  for (const r of counted) {
    if (!parts.has(r[level])) parts.set(r[level], new Map());
    const at = parts.get(r[level]);
    at.set(r.kind, (at.get(r.kind) || 0) + r.sessions);
  }
  const usedBy = new Map();
  for (const r of links) {
    if (!usedBy.has(r[level])) usedBy.set(r[level], []);
    usedBy.get(r[level]).push(r);
  }
  return fold(counted, level).map((row) => ({
    ...row,
    parts: [...parts.get(row.id)].sort((a, b) => b[1] - a[1]).map(([id]) => id),
    used: fold(usedBy.get(row.id) || [], used).sort(bySessions),
    installed: level === "agent" && installed ? installed.get(row.id)?.installed ?? null : null,
  })).sort(bySessions);
}

/** Installed products with nothing of theirs in the block (lib/fleetBreakdown.js installedNotUsed), as rows with no sessions. */
export function unusedRows(installed, block) {
  if (!installed || !block) return [];
  return installedNotUsed(installed, block.groups).map((i) => ({ id: installedKey(i.name), name: i.name, sessions: 0, parts: [], used: [], providers: [], installed: i.installed, unused: true }));
}

/** Each row's sessions in another block (the one before), and of what it used, by id; null where that block is unread. */
export function beforeOf(subject, block, scope = null) {
  if (!block) return null;
  return new Map(rowsOf(subject, block, scope).map((r) => [r.id, { sessions: r.sessions, used: new Map(r.used.map((u) => [u.id, u.sessions])) }]));
}

/** Whether a row is new: sessions in this block and none in the block before (`before`, beforeOf), unknown and so not new where that is unread. */
export const isNew = (row, before) => Boolean(before) && row.sessions > 0 && !(before.get(row.id)?.sessions > 0);

/**
 * Rows in the order asked: the most sessions first ("most"), the fewest
 * first ("fewest"), or the new ones first, then the rest, each the most
 * sessions first ("newest").
 */
export function sortRows(rows, sort = "most", before = null) {
  if (sort === "fewest") return [...rows].sort((a, b) => a.sessions - b.sessions || a.name.localeCompare(b.name));
  const most = [...rows].sort(bySessions);
  if (sort === "newest") return [...most.filter((r) => isNew(r, before)), ...most.filter((r) => !isNew(r, before))];
  return most;
}

/**
 * The first `first` rows and `pages` more of `step` each, and the rest as
 * one: `rest` its count and sessions, or null where none is left or one
 * would be, which is shown as itself.
 */
export function paged(rows, pages = 0, first = SHOWN, step = STEP) {
  const end = first + pages * step;
  if (rows.length - end <= 1) return { shown: rows, rest: null };
  const tail = rows.slice(end);
  return { shown: rows.slice(0, end), rest: { count: tail.length, sessions: sum(tail) } };
}

/** The pages `paged` needs to show the row at `index`. */
export const pagesTo = (index, first = SHOWN, step = STEP) => (index < first ? 0 : Math.ceil((index + 1 - first) / step));

/**
 * Whether a change carries weight: at least doubled or halved, and moving at
 * least MIN_SHARE of `whole`. Something new (nothing before) is not; its
 * arrow says so.
 */
export function bigChange(now, then, whole = 0) {
  if (!then) return false;
  return (now >= 2 * then || now * 2 <= then) && Math.abs(now - then) >= whole * MIN_SHARE;
}

/**
 * A row's bar: what it used as parts of the row's own sessions, the most
 * first, each `size` its length in sessions. A session that used two is in
 * both, so where the parts add up to more than the row they are scaled down
 * to it; where they add up to less (sessions with no model), the rest is one
 * part with no name (`rest`). Past the first `top`, what is left is one part
 * with no name (`more`, how many it holds), unless one would be.
 */
export function segmentsOf(row, top = SEGMENTS) {
  const used = sum(row.used);
  const scale = used > row.sessions ? row.sessions / used : 1;
  const { shown, rest: more } = paged(row.used, 0, top);
  const parts = shown.map((u) => ({ id: u.id, name: u.name, sessions: u.sessions, size: u.sessions * scale }));
  if (more) parts.push({ id: "more", name: "", more: more.count, sessions: more.sessions, size: more.sessions * scale });
  return used < row.sessions ? [...parts, { id: "rest", rest: true, name: "", sessions: row.sessions - used, size: row.sessions - used }] : parts;
}
